import React from "react";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import SEO from "@/components/SEO";
import { Youtube } from "lucide-react";

const FILTERS = ["All", "Concerts", "Recitals"];

const IMAGES = [
    // Recitals — piano & classical performance
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/5dc2wal6__MG_9780.jpeg", cat: "Recitals", caption: "Student electric guitar recital performance" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/i5hwvxv0__MG_9754.jpeg", cat: "Recitals", caption: "Piano recital — solo performance at Ihao Music House Delhi" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/jbplljka__MG_9785.jpeg", cat: "Recitals", caption: "Classical piano recital — student soloist" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/qrmceuqd__MG_9746.jpeg", cat: "Recitals", caption: "Piano recital — graceful performance on stage" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/17ozlv3n__MG_9736.jpeg", cat: "Recitals", caption: "Piano recital — advanced student performance" },
    // Concerts — live band performances
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/5x59kky0_IMG_3795.jpeg", cat: "Concerts", caption: "Student guitar performance — annual concert" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/2b0bnls5_IMG_3796.jpeg", cat: "Concerts", caption: "Student bassist on stage — showcase night" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/yq449axb_IMG_3798.jpeg", cat: "Concerts", caption: "Young guitarist live — termly recital" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/o12j23ly_IMG_3797.jpeg", cat: "Concerts", caption: "Student electric guitar solo on stage" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/cpdhitsm_IMG_3801.jpeg", cat: "Concerts", caption: "Faculty bassist — live showcase night" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/sjk6p2zu_IMG_3787.jpeg", cat: "Concerts", caption: "Christmas ensemble — student choir with violins on stage" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/4w3snf8c_DSC_8672.jpeg", cat: "Concerts", caption: "Rock ensemble under blue stage lights — annual concert" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/g4j3s1fv_DSC_8686.jpeg", cat: "Concerts", caption: "Student bassist centre stage — live showcase" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/be0q56ji_DSC03963.jpeg", cat: "Concerts", caption: "Ihao Music House Delhi student concert performance" },
    { src: "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/z3iy90xk_DSC03846.jpeg", cat: "Concerts", caption: "Live student band — annual showcase night" },
];

export default function Gallery() {
    const [filter, setFilter] = React.useState("All");
    const [lightbox, setLightbox] = React.useState(null);

    const filtered = filter === "All" ? IMAGES : IMAGES.filter((x) => x.cat === filter);

    return (
        <div data-testid="page-gallery">
            <SEO
                title="Gallery | Studio, Concerts & Recitals at Ihao Music House Delhi"
                description="Look inside the Ihao Music House Delhi studio — concerts, recitals, student performances and everyday moments in our Malviya Nagar music academy."
                path="/gallery"
            />
            <PageHero
                eyebrow="Gallery"
                title="A look inside the studio."
                subtitle="Concerts, recitals, studio sessions and the everyday moments that make Ihao Music House what it is."
            />

            {/* FILTERS */}
            <section className="relative py-10 bg-background border-b border-white/5 sticky top-20 z-30 backdrop-blur bg-black/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            data-testid={`filter-${f.toLowerCase()}`}
                            onClick={() => setFilter(f)}
                            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${
                                filter === f ? "bg-gold text-black border-gold" : "text-white/70 border-white/15 hover:border-gold/40 hover:text-white"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </section>

            {/* GRID */}
            <section className="relative py-16 md:py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filtered.map((img, i) => (
                            <button
                                key={img.src + i}
                                data-testid={`gallery-img-${i}`}
                                onClick={() => setLightbox(img)}
                                className="group relative overflow-hidden rounded-xl border border-white/10 bg-neutral-900 aspect-square focus:outline-none focus:border-gold"
                            >
                                <img src={img.src} alt={img.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold/90">{img.cat}</div>
                                    <div className="text-sm text-white font-semibold">{img.caption}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* VIDEO SECTION */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-3 mb-10">
                        <Youtube className="w-6 h-6 text-gold" />
                        <h2 className="font-display text-2xl md:text-4xl text-white font-semibold">Watch our students perform</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {["reXdNyBpEmQ", "CE_uQDXu0tM"].map((id, i) => (
                            <div key={i} className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
                                <iframe
                                    title={`Student performance ${i + 1}`}
                                    src={`https://www.youtube.com/embed/${id}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LIGHTBOX */}
            {lightbox && (
                <div
                    data-testid="gallery-lightbox"
                    onClick={() => setLightbox(null)}
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur flex items-center justify-center p-4 cursor-zoom-out"
                >
                    <button onClick={() => setLightbox(null)} aria-label="Close" className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-gold">×</button>
                    <figure className="max-w-5xl max-h-[90vh] flex flex-col items-center gap-4">
                        <img src={lightbox.src} alt={lightbox.caption} className="max-h-[80vh] w-auto rounded-2xl border border-white/10" />
                        <figcaption className="text-center">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-gold">{lightbox.cat}</div>
                            <div className="text-sm text-white font-semibold mt-1">{lightbox.caption}</div>
                        </figcaption>
                    </figure>
                </div>
            )}

            <CTABanner />
        </div>
    );
}
